/**
 * UnitToggle — small chip-style toggle to switch a persisted inventory line
 * between two display/input modes:
 *  - 'purchase' : the unit `capacity_value` is expressed per (Sachet, Pack, Bidon, Plaque, …)
 *  - 'base'     : the canonical base unit of the industrial product (kg, L, pièce, …)
 *
 * Variants:
 *  - <UnitToggle /> : desktop (text-[10px], emerald/slate styling, accessible labels)
 *  - <MobileUnitToggle /> : compact variant for the mobile cards (text-[9px])
 *
 * The component is purely presentational: state changes go through the
 * `onToggle(inv, newMode)` callback supplied by the parent. The parent persists
 * the change to the backend and updates the local state.
 */
import React from 'react';

function ToggleBase({ inv, mode, purchaseUnitLabel, baseUnit, onToggle, disabled, sizeClass }) {
    return (
        <div className="inline-flex items-center bg-slate-100 rounded p-0.5 gap-0.5" data-testid={`unit-toggle-${inv.id}`}>
            <button
                type="button"
                onClick={() => onToggle(inv, 'purchase')}
                className={`${sizeClass} font-medium rounded transition-colors ${
                    mode === 'purchase' ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:text-slate-700'
                }`}
                disabled={disabled}
                title={`Saisir en ${purchaseUnitLabel}`}
            >
                {purchaseUnitLabel}
            </button>
            <button
                type="button"
                onClick={() => onToggle(inv, 'base')}
                className={`${sizeClass} font-medium rounded transition-colors ${
                    mode === 'base' ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:text-slate-700'
                }`}
                disabled={disabled}
                title={`Saisir en ${baseUnit}`}
            >
                {baseUnit}
            </button>
        </div>
    );
}

export function UnitToggle({ inv, getInvItemData, onToggle, canEdit }) {
    const { purchaseUnitLabel, baseUnit } = getInvItemData(inv);
    const mode = inv.unit_mode || 'purchase';
    if (purchaseUnitLabel === baseUnit || !inv.supplier_product_id) {
        return <span className="text-[10px] text-slate-400">{baseUnit}</span>;
    }
    return (
        <ToggleBase
            inv={inv}
            mode={mode}
            purchaseUnitLabel={purchaseUnitLabel}
            baseUnit={baseUnit}
            onToggle={onToggle}
            disabled={!canEdit}
            sizeClass="px-1.5 py-0.5 text-[10px]"
        />
    );
}

export function MobileUnitToggle({ inv, getInvItemData, onToggle, canEdit }) {
    const { purchaseUnitLabel, baseUnit } = getInvItemData(inv);
    const mode = inv.unit_mode || 'purchase';
    if (purchaseUnitLabel === baseUnit || !inv.supplier_product_id) {
        return <span className="text-[10px] text-slate-400">{baseUnit}</span>;
    }
    return (
        <ToggleBase
            inv={inv}
            mode={mode}
            purchaseUnitLabel={purchaseUnitLabel}
            baseUnit={baseUnit}
            onToggle={onToggle}
            disabled={!canEdit}
            sizeClass="px-1.5 py-0.5 text-[9px]"
        />
    );
}
